/**
 * Between the domain's uniform currency rows and the shape live events and booking
 * services put on the wire.
 * Pure TypeScript - no React imports.
 *
 * Those two offering types carry the base currency as top-level scalars —
 * `callPriceCurrency`, `callPrice`, `minCallPrice`. The *request* forbids it from also
 * appearing in `currencies[]`; the contract enforces that with
 * `CurrenciesDistinctFromCallPriceCurrency`. The *response* does not honour the same rule
 * — a booking service returns the base currency inside `currencies[]` regardless — so
 * both directions here drop the duplicate by currency code rather than assume it away.
 * Digital Products and Product Services put every row in the array instead. The domain
 * keeps rows uniform either way, with row one understood to be the base, and this is
 * where the two base-scalar types reshape.
 *
 * See `docs/adr/0002-pricing-slice-uniform-currency-rows.md`.
 *
 * Shared by the live event and booking service mappers rather than written twice: the two
 * spell the split identically, and a bug here fails quietly — the server strips a
 * duplicated base currency defensively, so a duplicate survives manual testing.
 */

import {sameCurrency} from "../../domain";
import type {
  OfferingFormCurrencyRow,
  OfferingFormPriceType,
} from "./offering-form-pricing.mapper";

/**
 * The currency the two base-scalar types fall back to when an offering has no rows at
 * all — what their forms have always sent for a Free offering.
 */
const NO_ROWS_CURRENCY = "NGN";

/**
 * One priced currency on the wire.
 *
 * Unlike a form row, both amounts here are settled: a row still being filled in can hold
 * no price, but nothing leaves the application without one.
 */
export interface BaseScalarCurrency {
  callPriceCurrency: string;
  callPrice: number;
  /** The Minimum Price. Present only under Pay What You Want. */
  minCallPrice?: number;
}

/**
 * An offering's pricing as live events and booking services put it on the wire: the base
 * currency in the scalars, every other currency in the array.
 */
export interface BaseScalarPricing {
  callPriceCurrency: string;
  callPrice: number;
  /** The Minimum Price of the base currency. Present only under Pay What You Want. */
  minCallPrice?: number;
  /** Every currency except the base one. */
  currencies: BaseScalarCurrency[];
}

/**
 * Splits uniform currency rows into the base scalars and the rest.
 *
 * Row one is the base currency and does not appear in the returned array. A row's
 * Minimum Price is omitted rather than sent as a zero the coach never typed.
 *
 * @param rows - the offering's currency rows, base first
 */
export function toBaseScalarPricing(
  rows: readonly OfferingFormCurrencyRow[]
): BaseScalarPricing {
  const [base, ...rest] = rows;

  if (!base) {
    return {callPriceCurrency: NO_ROWS_CURRENCY, callPrice: 0, currencies: []};
  }

  return {
    callPriceCurrency: base.callPriceCurrency,
    callPrice: toAmount(base.callPrice),
    ...minimumPriceOf(base),
    currencies: rest.map((row) => ({
      callPriceCurrency: row.callPriceCurrency,
      callPrice: toAmount(row.callPrice),
      ...minimumPriceOf(row),
    })),
  };
}

/**
 * A row's Minimum Price, or nothing at all.
 *
 * A row the coach has not typed a minimum into holds `""` rather than `undefined`, because
 * react-hook-form falls back to `defaultValues` for a field it holds no value for. Both
 * mean "not entered", and neither may be sent as the zero it would coerce to.
 */
function minimumPriceOf(row: OfferingFormCurrencyRow): {minCallPrice?: number} {
  const entered = Number(row.minCallPrice);

  return row.minCallPrice === undefined ||
    (row.minCallPrice as unknown) === "" ||
    !Number.isFinite(entered)
    ? {}
    : {minCallPrice: entered};
}

/**
 * The base scalars and the rest of the rows, as a form holds them rather than as they go
 * on the wire — so a row the coach is still filling in, with no price yet, is allowed.
 */
export interface BaseScalarPricingRows {
  callPriceCurrency: string;
  callPrice?: number;
  minCallPrice?: number;
  currencies: OfferingFormCurrencyRow[];
}

/**
 * Folds the base scalars back in front of the array, giving the uniform rows the domain
 * and the shared Pay What You Want section both work in.
 *
 * A base currency the array also carries is dropped rather than shown twice. The contract
 * forbids the duplicate on the request, but the response sends it, so this is the normal
 * case and not a tolerance for malformed data.
 */
export function toUniformCurrencyRows(
  pricing: BaseScalarPricingRows
): OfferingFormCurrencyRow[] {
  return [
    {
      callPriceCurrency: pricing.callPriceCurrency,
      callPrice: pricing.callPrice,
      ...(pricing.minCallPrice === undefined ? {} : {minCallPrice: pricing.minCallPrice}),
    },
    ...pricing.currencies.filter(
      (row) => !sameCurrency(row.callPriceCurrency, pricing.callPriceCurrency)
    ),
  ];
}

/**
 * An amount as a number. The DOM hands amounts over as strings, and a form that has been
 * through an input holds them that way until something coerces them.
 */
function toAmount(amount: number | undefined): number {
  return Number(amount ?? 0);
}

/**
 * The pricing slice of a live event or booking service form, as the payload reads it.
 */
export interface BaseScalarPricingFormValues {
  callPriceType: OfferingFormPriceType;
  callPriceCurrency: string;
  callPrice: number;
  currencies: OfferingFormCurrencyRow[];
}

/** What a live event or booking service sends for its pricing. */
export type BaseScalarPricingPayload = BaseScalarPricing & {isPayWhatYouWant: boolean};

/**
 * An offering's pricing, ready to send.
 *
 * Under Pay What You Want the form holds every currency in `currencies`, base first, and
 * this splits row one back into the scalars — the base currency must not also appear in
 * the array. Free and Single Fee send exactly what they always have, base row and all:
 * moving those two is not what this feature is for, and the server has tolerated the
 * duplicate for as long as they have sent it.
 *
 * @param values - the pricing fields of the offering form
 */
export function toBaseScalarPricingPayload(
  values: BaseScalarPricingFormValues
): BaseScalarPricingPayload {
  if (values.callPriceType === "free") {
    return {
      isPayWhatYouWant: false,
      callPrice: 0,
      callPriceCurrency: NO_ROWS_CURRENCY,
      currencies: [],
    };
  }

  if (values.callPriceType === "pwyw") {
    return {isPayWhatYouWant: true, ...toBaseScalarPricing(values.currencies)};
  }

  const base = {
    callPrice: Number(values.callPrice),
    callPriceCurrency: values.callPriceCurrency,
  };

  return {
    isPayWhatYouWant: false,
    ...base,
    // No Minimum Price under Single Fee, so a minimum the coach typed during a visit to
    // Pay What You Want cannot leak out under a model that has no meaning for it.
    currencies: [
      base,
      ...values.currencies.map((row) => ({
        callPriceCurrency: row.callPriceCurrency,
        callPrice: toAmount(row.callPrice),
      })),
    ],
  };
}

/**
 * Reads a saved live event or booking service's Pricing Model back in the form's own
 * encoding.
 *
 * The wire states only Pay What You Want outright; Free and Single Fee stay derived from
 * whether the offering is priced at all, exactly as they were before this feature.
 *
 * @param offering - the offering as the edit pages hold it
 * @param isPwywEnabled - the feature flag; with it off, an offering saved as Pay What You
 *   Want reads back as Single Fee
 */
export function readBaseScalarPriceType(
  offering: {callPrice: number; isPayWhatYouWant?: boolean},
  isPwywEnabled: boolean
): OfferingFormPriceType {
  if (offering.callPrice === 0) {
    return "free";
  }

  return isPwywEnabled && offering.isPayWhatYouWant ? "pwyw" : "paid";
}

/**
 * One saved currency row, as the two edit pages read it back.
 *
 * Snake_case because that is the legacy response shape both edit pages still hold — they
 * adapt the v2 domain entity back into it rather than the other way around.
 */
export interface SavedBaseScalarCurrency {
  id?: number;
  currency: string;
  amount: number;
  minCallPrice?: number;
  is_auto_converted?: boolean;
  /**
   * Absent from a booking service response entirely, and unreliable elsewhere. The base
   * row is found by matching `callPriceCurrency`; this is only a fallback.
   */
  is_base?: boolean;
}

/** A saved live event or booking service, as far as its pricing goes. */
export interface SavedBaseScalarOffering {
  callPrice: number;
  callPriceCurrency: string;
  minCallPrice?: number;
  isPayWhatYouWant?: boolean;
  currencies: SavedBaseScalarCurrency[];
}

/**
 * Reads a saved live event or booking service's pricing back as its offering form holds
 * it — the same shape `toBaseScalarPricingPayload` sends, which is what makes the
 * round-trip a round-trip.
 *
 * Auto-converted currencies are left out: they are not rows the coach priced, and the
 * form has never offered them for editing.
 *
 * Under Pay What You Want the rows come back as one uniform list with the base row first,
 * because every row needs a Minimum Price field of its own. Under the other two models
 * the base row stays in the scalars, exactly where these two types have always kept it.
 *
 * @param offering - the offering as the edit pages hold it
 * @param isPwywEnabled - the feature flag; with it off, an offering saved as Pay What You
 *   Want reads back as Single Fee
 */
export function toBaseScalarPricingFormValues(
  offering: SavedBaseScalarOffering,
  isPwywEnabled: boolean
): BaseScalarPricingFormValues {
  const callPriceType = readBaseScalarPriceType(offering, isPwywEnabled);

  // The base row lives in the scalars on the wire and is repeated in the array on the way
  // back. Either is the same row, and the currency code is what ties them together:
  // `is_base` is absent from a booking service response entirely, so nothing may turn on
  // it. It is still believed where a response does send it.
  const baseRow =
    offering.currencies.find((row) =>
      sameCurrency(row.currency, offering.callPriceCurrency)
    ) ?? offering.currencies.find((row) => row.is_base);
  const callPriceCurrency =
    baseRow?.currency || offering.callPriceCurrency || NO_ROWS_CURRENCY;
  const callPrice = baseRow?.amount || offering.callPrice || 0;
  const minCallPrice = baseRow?.minCallPrice ?? offering.minCallPrice;

  // Excluded by currency code, not by `id`. A booking service's currency rows carry no
  // `id` at all — `ServiceCurrencyOptionResponseDto` has no such field — so an `id`
  // filter here silently dropped every currency the coach had priced by hand.
  const pricedByHand = offering.currencies
    .filter((row) => !row.is_auto_converted)
    .filter((row) => !sameCurrency(row.currency, callPriceCurrency))
    .map((row) => ({
      callPriceCurrency: row.currency,
      callPrice: row.amount,
      ...(row.minCallPrice === undefined ? {} : {minCallPrice: row.minCallPrice}),
    }));

  return {
    callPriceType,
    callPriceCurrency,
    callPrice,
    currencies:
      callPriceType === "pwyw"
        ? toUniformCurrencyRows({
            callPriceCurrency,
            callPrice,
            minCallPrice,
            currencies: pricedByHand,
          })
        : pricedByHand,
  };
}
